import React, { FC, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import moment from 'moment';

import TimesheetRepository from 'src/api/repositories/ess/v1/timesheet.repository';
import { AddIcon, DollarSignIcon, EmptyState, ServerError500Icon } from 'src/shared/components';
import { API_DATE_FORMAT } from 'src/shared/constants';
import { DurationFormatEnums, PermissionsEnums } from 'src/shared/enums';
import { ComponentWithPermissions } from 'src/shared/hocs';
import { useTranslate } from 'src/shared/providers';
import { formatDuration } from 'src/shared/utils';

import { QuickEntryAddModal } from './QuickEntryAddModal';
import { QuickEntryAddTypeEnums } from './QuickEntryCard.enums';
import { QuickEntryItem } from './QuickEntryItem';

export const QuickEntryCard: FC = () => {
  const { t } = useTranslate();

  const [addOpen, setAddOpen] = useState(false);
  const [addType, setAddType] = useState(QuickEntryAddTypeEnums.DURATION);

  const today = moment().format(API_DATE_FORMAT);

  const {
    data: timesheetDashboard,
    isLoading,
    isFetching,
    error,
  } = TimesheetRepository.endpoints.useGetTimesheetDashboardsQuery({
    startDate: today,
    endDate: today,
  });

  const hasEntries = timesheetDashboard?.timesheetDayEntries.some(
    (timesheetDashboard) => !!timesheetDashboard.entries.length,
  );

  const loading = isLoading || isFetching;

  const handleAddOpen = (type: QuickEntryAddTypeEnums) => {
    setAddOpen(true);
    setAddType(type);
  };

  if (loading) {
    return <Skeleton className="rounded border" variant="rectangular" animation="wave" height={320} />;
  }

  return (
    <>
      <Paper sx={{ height: '100%', padding: '32px 0', overflow: 'hidden' }}>
        <Stack direction="column" gap={2}>
          <Stack direction="column" gap={2} padding="0 32px">
            <h4 className="pb-0 mb-0 me-1">{t('timesheets.quickEntry')}</h4>
            <Divider />
          </Stack>
          {!!error && (
            <EmptyState className="m-0" message={t('global.oopsAnErrorOccurred')} icon={<ServerError500Icon />} />
          )}
          {!error && (
            <>
              <Typography fontWeight="bold" sx={{ padding: '0 32px' }}>
                {t('timesheets.todaysEntries')}
              </Typography>
              <Stack direction="column" gap={2} maxHeight={320} overflow="auto" p="0 32px">
                {!hasEntries && (
                  <EmptyState
                    size="small"
                    title={t('timesheets.noEntriesYet')}
                    message={t('timesheets.noEntriesToShowForNow')}
                    className="m-0"
                  />
                )}
                {hasEntries &&
                  timesheetDashboard?.timesheetDayEntries.map(({ entries, durationTotal, expenseTotal }, index) => (
                    <Box key={index} display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                      {entries.map((entry) => (
                        <QuickEntryItem key={entry.userEditId} entry={entry} />
                      ))}
                      <Stack direction="row" gridRow="1" gridColumn="2" gap={2}>
                        {durationTotal ? (
                          <TotalItem sx={{ flex: 3 }}>
                            <Typography fontSize="1.5rem" fontWeight={500} textAlign="right" lineHeight={1} flex={2}>
                              {formatDuration(durationTotal, DurationFormatEnums.T_24_HOURS, 'seconds')}
                            </Typography>
                            <Typography fontSize="0.75rem" color="text.secondary" whiteSpace="pre-wrap" flex={1}>
                              {t('timesheets.totalHours')}
                            </Typography>
                          </TotalItem>
                        ) : (
                          <Box flex={3} />
                        )}
                        {expenseTotal ? (
                          <TotalItem sx={{ flex: 2 }}>
                            <Typography fontSize="1.5rem" fontWeight={500} textAlign="right" lineHeight={1} flex={1}>
                              {expenseTotal.toFixed(2)}
                            </Typography>
                            <Typography fontSize="0.75rem" color="text.secondary" whiteSpace="wrap" flex={1}>
                              {t('global.total')} ($)
                            </Typography>
                          </TotalItem>
                        ) : (
                          <Box flex={2} />
                        )}
                      </Stack>
                    </Box>
                  ))}
              </Stack>
            </>
          )}
          <ComponentWithPermissions permissions={PermissionsEnums.SHIFTMATCH_EDIT}>
            <Stack direction="row" justifyContent="flex-end" gap={2} padding="0 32px">
              <ComponentWithPermissions permissions={PermissionsEnums.WTS_INPUT_EXPENSE}>
                <Button
                  color="secondary"
                  startIcon={<DollarSignIcon />}
                  onClick={() => handleAddOpen(QuickEntryAddTypeEnums.EXPENSE)}
                >
                  {t('timesheets.addExpense')}
                </Button>
              </ComponentWithPermissions>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleAddOpen(QuickEntryAddTypeEnums.DURATION)}
              >
                {t('timesheets.addTime')}
              </Button>
            </Stack>
          </ComponentWithPermissions>
        </Stack>
      </Paper>
      <QuickEntryAddModal type={addType} open={addOpen} onClose={() => setAddOpen(false)} />
    </>
  );
};

const TotalItem = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 16,
  border: `1px solid ${theme.palette.primary.main}`,
  borderRadius: 10,
  padding: 8,
}));
